'use client';

import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import Link from 'next/link';
import React, { useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';

import { useAuth } from '@/entities/user';
import { useToast } from '@/features/toast';
import { Button } from '@/shared/ui/button';
import { Container } from '@/shared/ui/container';
import { Loader } from '@/shared/ui/loader';
import { Stepper2 } from '@/shared/ui/stepper2';

import { PAYMENT_METHODS, PAYMENT_STEPS, PRODUCT_TYPES } from '../../const';
import { usePaymentStore } from '../../store';
import { ICoursePaymentData, PaymentMethodType } from '../../types';
import { PaymentStep1 } from '../step-1';
import { PaymentStep2 } from '../step-2';

interface PaymentStepperFormProps {
	productId: string;
	paymentData: ICoursePaymentData;
	tariffId: string;
	paymentMethod: string;
	isSubscription?: boolean;
	promocode?: string;
	subscriptionId?: string;
}

export const PaymentStepperForm = ({
	productId,
	paymentData,
	tariffId,
	paymentMethod,
	isSubscription = false,
	promocode = '',
}: PaymentStepperFormProps) => {
	const { isAuthorized, loading } = useAuth();
	const { addToast } = useToast();
	const {
		activeStep,
		activeStepName,
		setActiveStep,
		goPrevStep,
		initialize,
		submitForm,
		steps,
	} = usePaymentStore();

	// Определяем шаги в зависимости от авторизации
	const availableSteps = isAuthorized ? ['Способы оплаты', 'Оплата'] : steps;
	const currentStepName = isAuthorized
		? availableSteps[activeStep]
		: activeStepName;
	// paymentData — новый объект при каждом ре-рендере серверного компонента
	// (например, после Router Cache refresh), поэтому переинициализацию визарда
	// триггерим только по примитивным идентификаторам, а не по ссылке на paymentData.
	const initKeyRef = useRef<string | null>(null);
	useEffect(() => {
		const initKey = JSON.stringify([
			productId,
			tariffId,
			paymentMethod,
			isAuthorized,
			isSubscription,
			promocode,
		]);

		if (initKeyRef.current === initKey) {
			return;
		}
		initKeyRef.current = initKey;

		const availablePaymentMethods = paymentData.paymentMethodsData.map(
			(method) => method.id,
		);
		const validPaymentMethod =
			paymentMethod && availablePaymentMethods.includes(paymentMethod as any)
				? paymentMethod
				: availablePaymentMethods[0];

		initialize({
			productType: isSubscription
				? PRODUCT_TYPES.SUBSCRIPTION
				: PRODUCT_TYPES.TARIFF,
			productId: tariffId,
			startDate: paymentData.startDate,
			tariffId,
			paymentMethod: validPaymentMethod as PaymentMethodType,
			isAuthorized,
			promoCode: promocode || '',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- paymentData сознательно исключён, см. комментарий выше
	}, [
		productId,
		initialize,
		tariffId,
		paymentMethod,
		isAuthorized,
		isSubscription,
		setActiveStep,
		promocode,
	]);

	const handleStepClick = (step: number) => {
		if (step >= availableSteps.length) {
			return;
		}

		const { values, setErrors, errors } = usePaymentStore.getState();

		// Если пытаемся перейти на шаг оплаты и пользователь не авторизован, валидируем email
		if (!isAuthorized && step === PAYMENT_STEPS.PAYMENT_METHOD) {
			const emailSchema = z.string().email('Некорректный e-mail');
			const result = emailSchema.safeParse(values.userEmail);

			if (!result.success) {
				setErrors({
					...errors,
					userEmail: [result.error.errors[0].message],
				});
				return;
			}
		}

		setActiveStep(step);
	};

	const paymentMethodsData = useMemo(() => {
		return paymentData.paymentMethodsData.sort((a) =>
			a.id === PAYMENT_METHODS.FULL ? -1 : 0,
		);
	}, [paymentData.paymentMethodsData]);

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const { success, error } = await submitForm();
		if (!success) {
			addToast('Ошибка', error, 'error');
		}
	};

	// Показываем лоадер пока определяется статус авторизации
	if (loading) {
		return (
			<div className="flex h-full flex-1 items-center justify-center">
				<Loader type="fullscreen" />
			</div>
		);
	}
	return (
		<div className="flex h-full flex-col pb-8">
			<div className="sticky top-0 z-10 flex w-full items-center justify-between border-b border-gray-4 bg-white px-4 py-2 md:py-3">
				<Button
					onClick={goPrevStep}
					variant="outline"
					className={clsx(
						'h-[40px] w-[40px] p-2 md:hidden',
						activeStep === 0 && 'invisible',
					)}
				>
					<ArrowLeftIcon className="h-4 w-4 flex-shrink-0" />
				</Button>

				<Stepper2
					className="hidden md:flex"
					items={availableSteps}
					activeIndex={activeStep}
					onItemClick={handleStepClick}
					disableClickIndexes={[availableSteps.length - 1]}
				/>
				<div className="flex-1 px-4 text-center font-semibold md:hidden">
					{currentStepName}
				</div>
				<Link
					href={`/${isSubscription ? 'algorithms#price' : productId}`}
					className="ml-auto"
				>
					<Button variant="outline" className="h-[40px] w-[40px] p-2">
						<XMarkIcon className="h-4 w-4 flex-shrink-0" />
					</Button>
				</Link>
			</div>
			<Container>
				<h1 className="mb-8 mt-16 hidden text-center text-[54px] font-semibold md:block">
					{currentStepName}
				</h1>
				{paymentData && (
					<>
						{!isAuthorized && (
							<div
								className={clsx(
									'm-auto w-full max-w-lg transition-opacity duration-300',
									{
										'opacity-100': activeStep === PAYMENT_STEPS.COURSE_INFO,
										'absolute h-0 overflow-hidden opacity-0':
											activeStep !== PAYMENT_STEPS.COURSE_INFO,
									},
								)}
							>
								<PaymentStep1 />
							</div>
						)}
						<form
							onSubmit={handleSubmit}
							className={clsx('transition-opacity duration-300', {
								'opacity-100': isAuthorized
									? activeStep === 0
									: activeStep === PAYMENT_STEPS.PAYMENT_METHOD,
								'absolute h-0 overflow-hidden opacity-0': isAuthorized
									? activeStep !== 0
									: activeStep !== PAYMENT_STEPS.PAYMENT_METHOD,
							})}
						>
							<PaymentStep2
								fullPrice={paymentData.fullPrice}
								loanPrice={paymentData.loanPrice}
								dolyamiPrice={paymentData.dolyamiPrice}
								name={paymentData.name}
								loanOptions={paymentData.loanOptions}
								courseName={paymentData.courseName}
								paymentMethodsData={paymentMethodsData}
								isSubscription={isSubscription}
								isPromoAllowed={paymentData.isPromoAllowed}
								isLongTermSubscription={paymentData.isLongTermSubscription}
								days={paymentData.days}
							/>
						</form>
					</>
				)}
			</Container>
		</div>
	);
};
